/**
 * GuideRenderer.jsx
 * =================
 *
 * Composant partagé pour afficher les guides utilisateurs (client / admin) avec un rendu
 * documentation moderne inspiré de https://techdocs.akamai.com :
 *  - Layout 3 colonnes : navigation gauche (sticky), contenu centré, mini-TOC droite (sticky)
 *  - Hiérarchie typographique épurée (H1 large avec filet rouge/vert, H2 avec liseré)
 *  - Espacement généreux, palette neutre, lecture confortable (≈ 720px de contenu)
 *  - Encadrés "ASTUCE / ATTENTION / IMPORTANT / À RETENIR / VOUS APPRENDREZ À" rendus
 *    en composants visuels (au lieu de tableaux DOCX bruts)
 *  - Scrollspy : surligne la section visible dans la nav et la mini-TOC
 *  - Breadcrumbs, recherche, version, dernière mise à jour, téléchargements PDF/DOCX
 *
 * Inspirations : Akamai TechDocs, Stripe Docs, Notion.
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Download, Printer, Search, FileText, Menu, X, ChevronRight, Home,
  ListOrdered, ArrowUp,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const slugify = (text) =>
  String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

// ---------------------------------------------------------------------------
// Helpers de post-traitement du HTML mammoth
// ---------------------------------------------------------------------------

/**
 * Détecte les "tables-encadrés" provenant du DOCX et les convertit DIRECTEMENT en
 * <aside class="callout"> stylés (HTML statique) pour qu'ils survivent aux re-renders React.
 * Le DOCX utilise <table><tr><td><p><strong>▶  ASTUCE<br/></strong>...</p></td></tr></table>
 * pour ses encadrés. On détecte le type d'encadré et on génère le HTML final.
 */
const CALLOUT_KIND_FOR_MARKER = (markerUpper) => {
  if (markerUpper.includes('ASTUCE')) return 'tip';
  if (markerUpper.includes('ATTENTION')) return 'warning';
  if (markerUpper.includes('IMPORTANT')) return 'important';
  if (markerUpper.includes('À RETENIR') || markerUpper.includes('A RETENIR')) return 'remember';
  if (markerUpper.includes('APPRENDREZ')) return 'learn';
  return 'note';
};

// Icônes Lucide en SVG inline (compatibles innerHTML — Lucide React ne fonctionne pas
// dans du HTML dynamique).
const CALLOUT_ICONS = {
  tip:       '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/><path d="M9 18h6"/><path d="M10 22h4"/></svg>',
  warning:   '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>',
  important: '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 13c0 5-3.5 7.5-8 8.5C7.5 20.5 4 18 4 13V6l8-3 8 3z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>',
  note:      '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>',
  remember:  '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m9 12 2 2 4-4"/><path d="M19 21l-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>',
  learn:     '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>',
};

const transformCallouts = (html) => {
  return html.replace(
    /<table>\s*<tr>\s*<td>\s*<p><strong>([▶★⚠✅🔵]?\s*[A-ZÀ-Ÿ' ]+?)<br\s*\/?>\s*<\/strong>([\s\S]*?)<\/p>\s*<\/td>(?:\s*<td>\s*<\/td>)?\s*<\/tr>\s*<\/table>/g,
    (match, marker, body) => {
      const m = marker.trim().toUpperCase();
      const kind = CALLOUT_KIND_FOR_MARKER(m);
      const cfg = CALLOUT_STYLES[kind];
      const label = m.replace(/^[▶★⚠✅🔵\s]+/, '').trim() || cfg.label;
      const icon = CALLOUT_ICONS[kind] || CALLOUT_ICONS.note;
      const cleanBody = body.trim();
      return `<aside class="callout my-6 rounded-lg border-l-4 ${cfg.box} p-4 pl-5">
  <div class="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wide ${cfg.label_text}">
    <span class="callout-icon inline-flex">${icon}</span>
    <span>${label}</span>
  </div>
  <div class="text-sm ${cfg.body_text} leading-relaxed callout-body">${cleanBody}</div>
</aside>`;
    }
  );
};

/**
 * Ajoute les ids sur les H1/H2/H3 pour les ancres (TOC + scrollspy).
 * Supporte les H1/H2 contenant des sous-balises (ex: <h1><strong>Titre</strong></h1>).
 */
const addHeadingAnchors = (html) =>
  html.replace(/<(h[1-3])>([\s\S]*?)<\/\1>/g, (m, tag, inner) => {
    // Nettoie les sous-balises pour générer le slug
    const text = inner.replace(/<[^>]+>/g, '').trim();
    if (!text) return m;
    return `<${tag} id="${slugify(text)}">${inner}</${tag}>`;
  });

/**
 * Préfixe les URLs d'images relatives avec BACKEND_URL.
 */
const fixImageUrls = (html, backendUrl) =>
  html.replace(/src="(\/api\/uploads\/[^"]+)"/g, (m, p) => `src="${backendUrl}${p}"`);

/**
 * Numérote automatiquement les chapitres (H1) en ajoutant un préfixe "01. ", "02. " ...
 * Ne touche pas les H1 qui contiennent déjà une numérotation (ex: "1. Bienvenue").
 * Optionnel : on n'altère pas le texte d'origine mais on retourne juste les positions.
 */
const buildToc = (html) => {
  const toc = [];
  const re = /<(h[1-3])\s+id="([^"]+)"[^>]*>([\s\S]*?)<\/\1>/g;
  let m;
  let chapterCount = 0;
  while ((m = re.exec(html)) !== null) {
    const level = parseInt(m[1].slice(1), 10);
    const slug = m[2];
    const title = m[3].replace(/<[^>]+>/g, '').trim();
    if (!title) continue;
    if (level === 1) chapterCount += 1;
    toc.push({ level, slug, title, chapter: level === 1 ? chapterCount : null });
  }
  return toc;
};

// ---------------------------------------------------------------------------
// Composants visuels
// ---------------------------------------------------------------------------

// Styles statiques pour chaque type d'encadré.
// Tailwind JIT scanne ces fichiers et détecte chaque classe en dur.
const CALLOUT_STYLES = {
  tip: {
    label: 'ASTUCE',
    box: 'border-emerald-500 bg-emerald-50',
    label_text: 'text-emerald-700',
    body_text: 'text-emerald-950',
  },
  warning: {
    label: 'ATTENTION',
    box: 'border-amber-500 bg-amber-50',
    label_text: 'text-amber-700',
    body_text: 'text-amber-950',
  },
  important: {
    label: 'IMPORTANT',
    box: 'border-rose-500 bg-rose-50',
    label_text: 'text-rose-700',
    body_text: 'text-rose-950',
  },
  note: {
    label: 'NOTE',
    box: 'border-slate-400 bg-slate-50',
    label_text: 'text-slate-700',
    body_text: 'text-slate-900',
  },
  remember: {
    label: 'À RETENIR',
    box: 'border-emerald-600 bg-emerald-50',
    label_text: 'text-emerald-800',
    body_text: 'text-emerald-950',
  },
  learn: {
    label: 'VOUS APPRENDREZ À',
    box: 'border-sky-500 bg-sky-50',
    label_text: 'text-sky-700',
    body_text: 'text-sky-950',
  },
};

/**
 * Remplace les placeholders <div data-callout> par des composants React stylés.
 * On utilise une simple récriture innerHTML basée sur l'attribut.
 */
// (transformCallouts ci-dessus produit déjà directement le HTML stylé final ;
// pas besoin de post-traitement React.)

// ---------------------------------------------------------------------------
// Sous-composant : mini-TOC droite (On this page)
// ---------------------------------------------------------------------------
const OTP_ACTIVE = {
  emerald: 'border-l-2 border-emerald-500 text-emerald-700 font-medium',
  rose:    'border-l-2 border-rose-500 text-rose-700 font-medium',
};

const OnThisPage = ({ items, activeSlug, onClick, accentColor }) => {
  const h2s = items.filter((s) => s.level === 2);
  if (h2s.length === 0) return null;
  const activeCls = OTP_ACTIVE[accentColor] || OTP_ACTIVE.emerald;
  return (
    <div className="text-sm" data-testid="on-this-page">
      <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
        <ListOrdered className="w-3.5 h-3.5" />
        Sur cette page
      </h4>
      <ul className="space-y-1.5 border-l border-slate-200">
        {h2s.map((s) => {
          const active = s.slug === activeSlug;
          return (
            <li key={s.slug}>
              <button
                onClick={() => onClick(s.slug)}
                className={`block w-full text-left pl-3 -ml-px py-0.5 text-xs leading-snug transition-colors ${
                  active
                    ? activeCls
                    : 'text-slate-600 hover:text-slate-900 hover:border-l-2 hover:border-slate-400'
                }`}
                data-testid={`otp-${s.slug}`}
              >
                {s.title}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------

/**
 * Props :
 *   title            : titre affiché dans le header (ex: "Guide Client")
 *   subtitle         : version + dernière maj (ex: "Version 2.0 · Mai 2026")
 *   accentColor      : 'emerald' (client) | 'rose' (admin) | 'sky'
 *   IconBrand        : composant Lucide affiché dans le header
 *   breadcrumbs      : [{label, href?}]
 *   htmlEndpoint     : URL /api/docs/.../html
 *   metaEndpoint     : URL /api/docs/.../meta
 *   downloadDocxUrl  : URL DOCX
 *   backUrl          : URL retour
 *   backLabel        : libellé retour
 *   axiosClient      : (optionnel) instance axios avec auth header
 */
const GuideRenderer = ({
  title,
  accentColor = 'emerald',
  IconBrand,
  breadcrumbs = [],
  htmlEndpoint,
  metaEndpoint,
  downloadDocxUrl,
  backUrl,
  backLabel = "Retour",
  axiosClient,
  testIdPrefix = 'guide',
}) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const [html, setHtml] = useState('');
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSlug, setActiveSlug] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const contentRef = useRef(null);

  // ----- Chargement contenu -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let htmlText, metaJson;
        if (axiosClient) {
          const [a, b] = await Promise.all([
            axiosClient.get(htmlEndpoint),
            axiosClient.get(metaEndpoint),
          ]);
          htmlText = String(a.data);
          metaJson = b.data;
        } else {
          const [a, b] = await Promise.all([
            fetch(htmlEndpoint),
            fetch(metaEndpoint),
          ]);
          if (!a.ok || !b.ok) throw new Error('Impossible de charger le guide');
          htmlText = await a.text();
          metaJson = await b.json();
        }
        if (cancelled) return;
        const fixed = fixImageUrls(htmlText, BACKEND_URL);
        const withCallouts = transformCallouts(fixed);
        const withAnchors = addHeadingAnchors(withCallouts);
        setHtml(withAnchors);
        setMeta(metaJson);
      } catch (e) {
        if (e?.response?.status === 403) {
          setError('Accès réservé au personnel interne.');
        } else {
          setError(e?.message || 'Erreur de chargement');
        }
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [htmlEndpoint, metaEndpoint]);

  // ----- TOC complet (depuis le HTML traité) -----
  const fullToc = useMemo(() => (html ? buildToc(html) : []), [html]);

  // ----- TOC filtré (recherche) -----
  const filteredToc = useMemo(() => {
    if (!searchQuery.trim()) return fullToc;
    const q = searchQuery.toLowerCase();
    return fullToc.filter((s) => s.title.toLowerCase().includes(q));
  }, [fullToc, searchQuery]);

  // ----- Surligner les occurrences dans le contenu -----
  const displayedHtml = useMemo(() => {
    if (!searchQuery.trim() || !html) return html;
    try {
      const q = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`(${q})`, 'gi');
      return html.replace(/(>)([^<]+)(<)/g, (m, a, txt, b) =>
        `${a}${txt.replace(re, '<mark class="bg-amber-200 rounded px-0.5">$1</mark>')}${b}`
      );
    } catch {
      return html;
    }
  }, [html, searchQuery]);

  // ----- Post-traitement React : scrollspy uniquement (les callouts sont déjà
  // transformés au niveau string via transformCallouts) -----

  // ----- Scrollspy + back-to-top -----
  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 400);
      // Trouver le H2 le plus proche au-dessus de la zone visible
      const headings = contentRef.current?.querySelectorAll('h2[id]');
      if (!headings || headings.length === 0) return;
      const offset = 120;
      let current = '';
      headings.forEach((h) => {
        const rect = h.getBoundingClientRect();
        if (rect.top - offset <= 0) current = h.id;
      });
      if (current && current !== activeSlug) setActiveSlug(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [activeSlug, displayedHtml]);

  const scrollToSection = useCallback((slug) => {
    const el = document.getElementById(slug);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setSidebarOpen(false);
    }
  }, []);

  // ----- Couleurs accent : pré-construit pour Tailwind JIT (classes 100% statiques) -----
  const ACCENT_PRESETS = {
    emerald: {
      ring: 'bg-emerald-600 hover:bg-emerald-700',
      icon: 'text-emerald-600',
      navHover: 'hover:bg-emerald-50 hover:text-emerald-800',
      link: 'text-emerald-700 hover:text-emerald-800',
      chapterBadge: 'bg-emerald-100 text-emerald-800',
      backToTop: 'bg-emerald-600 hover:bg-emerald-700',
      activeNav: 'bg-slate-100 text-emerald-700',
      // prose customisé via classes article-level
      proseClass: 'prose-emerald-theme',
    },
    rose: {
      ring: 'bg-rose-600 hover:bg-rose-700',
      icon: 'text-rose-600',
      navHover: 'hover:bg-rose-50 hover:text-rose-800',
      link: 'text-rose-700 hover:text-rose-800',
      chapterBadge: 'bg-rose-100 text-rose-800',
      backToTop: 'bg-rose-600 hover:bg-rose-700',
      activeNav: 'bg-slate-100 text-rose-700',
      proseClass: 'prose-rose-theme',
    },
  };
  const accent = ACCENT_PRESETS[accentColor] || ACCENT_PRESETS.emerald;

  return (
    <div className="min-h-screen bg-white" data-testid={`${testIdPrefix}-renderer`}>
      {/* Top header (sticky) */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-slate-200 print:hidden">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
          <button
            className="lg:hidden p-2 rounded-md hover:bg-slate-100"
            onClick={() => setSidebarOpen((v) => !v)}
            data-testid="toggle-toc-btn"
            aria-label="Menu"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          {IconBrand && <IconBrand className={`w-6 h-6 flex-shrink-0 ${accent.icon}`} />}
          <div className="flex-1 min-w-0">
            {/* Breadcrumbs */}
            <nav aria-label="Fil d'Ariane" className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
              {breadcrumbs.map((b, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <ChevronRight className="w-3 h-3 text-slate-400" />}
                  {b.href ? (
                    <a href={b.href} className="hover:text-slate-900 transition-colors">{b.label}</a>
                  ) : (
                    <span className="text-slate-700 font-medium">{b.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
            <h1 className="text-sm sm:text-base font-semibold text-slate-900 truncate leading-tight">
              {title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.print()}
              data-testid="print-guide-btn"
              className="hidden sm:inline-flex text-slate-700"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimer
            </Button>
            {meta?.downloadUrls?.pdf && (
              <Button
                size="sm"
                asChild
                className={accent.ring}
                data-testid="download-pdf-btn"
              >
                <a href={`${BACKEND_URL}${meta.downloadUrls.pdf}`} target="_blank" rel="noreferrer">
                  <Download className="w-4 h-4 mr-2" />
                  PDF
                </a>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Body : 3 colonnes */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex gap-8 lg:gap-12">
        {/* Sidebar nav (TOC) */}
        <aside
          className={`${
            sidebarOpen
              ? 'fixed inset-y-0 left-0 z-30 w-80 bg-white shadow-2xl p-6 overflow-y-auto'
              : 'hidden'
          } lg:relative lg:block lg:w-64 xl:w-72 lg:flex-shrink-0 lg:bg-transparent lg:shadow-none lg:p-0 print:hidden`}
        >
          <div className="lg:sticky lg:top-20 py-8 lg:max-h-[calc(100vh-5rem)] lg:overflow-y-auto pr-2">
            {/* Recherche */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher…"
                className="pl-9 text-sm h-9 bg-slate-50 border-slate-200 focus:bg-white"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-guide-input"
              />
            </div>

            {/* Métadonnées */}
            {meta && (
              <div className="mb-6 pb-6 border-b border-slate-200">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                  Documentation
                </div>
                <div className="text-sm text-slate-900 font-medium">
                  Version {meta.version}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  Mis à jour le {meta.lastUpdated}
                </div>
              </div>
            )}

            {/* Sommaire */}
            <nav className="space-y-0.5" aria-label="Sommaire">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2 px-2">
                Sommaire
              </div>
              {loading && <p className="text-sm text-slate-500 px-2">Chargement…</p>}
              {!loading && filteredToc.length === 0 && (
                <p className="text-sm text-slate-500 italic px-2">Aucun résultat.</p>
              )}
              {filteredToc.map((s, idx) => (
                <button
                  key={`${s.slug}-${idx}`}
                  onClick={() => scrollToSection(s.slug)}
                  className={`group w-full text-left text-sm py-1.5 px-2 rounded-md transition-colors flex items-start gap-2 ${
                    s.level === 1
                      ? `font-semibold text-slate-900 ${accent.navHover}`
                      : s.level === 2
                        ? `pl-7 text-slate-600 ${accent.navHover}`
                        : `pl-12 text-slate-500 text-xs ${accent.navHover}`
                  } ${activeSlug === s.slug ? accent.activeNav : ''}`}
                  data-testid={`toc-${s.slug}`}
                >
                  {s.level === 1 && s.chapter !== null && (
                    <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${accent.chapterBadge} flex-shrink-0 mt-0.5`}>
                      {String(s.chapter).padStart(2, '0')}
                    </span>
                  )}
                  <span className="flex-1 leading-snug">{s.title}</span>
                </button>
              ))}
            </nav>

            {/* Téléchargements */}
            <div className="mt-8 pt-6 border-t border-slate-200 space-y-2 text-xs text-slate-600">
              {meta?.downloadUrls?.trainingPptx && (
                <a
                  href={`${BACKEND_URL}${meta.downloadUrls.trainingPptx}`}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-2 ${accent.link} font-medium`}
                  data-testid="download-training-pptx-btn"
                >
                  <FileText className="w-3.5 h-3.5" /> Présentation formation (PPTX)
                </a>
              )}
              {meta?.downloadUrls?.trainingPdf && (
                <a
                  href={`${BACKEND_URL}${meta.downloadUrls.trainingPdf}`}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-2 ${accent.link}`}
                  data-testid="download-training-pdf-btn"
                >
                  <FileText className="w-3.5 h-3.5" /> Présentation formation (PDF)
                </a>
              )}
              {downloadDocxUrl && (
                <a
                  href={downloadDocxUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={`flex items-center gap-2 ${accent.link}`}
                  data-testid="download-docx-btn"
                >
                  <FileText className="w-3.5 h-3.5" /> Guide (DOCX)
                </a>
              )}
              {backUrl && (
                <a href={backUrl} className={`flex items-center gap-2 ${accent.link}`}>
                  <Home className="w-3.5 h-3.5" /> {backLabel}
                </a>
              )}
            </div>
          </div>
        </aside>

        {/* Contenu central */}
        <main className="flex-1 min-w-0 py-8 lg:py-12">
          {loading && (
            <div className="text-slate-500">
              <div className="animate-pulse">Chargement du guide…</div>
            </div>
          )}
          {error && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-900 max-w-2xl">
              <h3 className="font-semibold">Guide momentanément indisponible</h3>
              <p className="text-sm mt-2">{error}</p>
              {meta?.downloadUrls?.pdf && (
                <p className="text-sm mt-3">
                  Vous pouvez{' '}
                  <a
                    href={`${BACKEND_URL}${meta.downloadUrls.pdf}`}
                    className="underline font-medium"
                    target="_blank"
                    rel="noreferrer"
                  >
                    télécharger la version PDF
                  </a>.
                </p>
              )}
            </div>
          )}
          {!loading && !error && (
            <article
              ref={contentRef}
              className={`
                max-w-[760px] mx-auto
                guide-prose ${accent.proseClass}
                prose prose-slate max-w-none
                prose-headings:scroll-mt-20 prose-headings:tracking-tight
                prose-h1:text-4xl prose-h1:font-bold prose-h1:text-slate-900 prose-h1:mt-16 prose-h1:mb-6 prose-h1:pb-4
                prose-h2:text-2xl prose-h2:font-semibold prose-h2:text-slate-900 prose-h2:mt-12 prose-h2:mb-4 prose-h2:pl-4
                prose-h3:text-lg prose-h3:font-semibold prose-h3:text-slate-800 prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-[15px] prose-p:leading-7 prose-p:text-slate-700
                prose-li:text-[15px] prose-li:leading-7 prose-li:text-slate-700 prose-li:my-1
                prose-ul:my-4 prose-ol:my-4
                prose-img:rounded-lg prose-img:shadow-md prose-img:border prose-img:border-slate-200 prose-img:mx-auto prose-img:my-6
                prose-table:text-sm prose-table:my-6 prose-table:rounded-lg prose-table:overflow-hidden prose-table:border prose-table:border-slate-200
                prose-th:bg-slate-50 prose-th:text-slate-800 prose-th:font-semibold prose-th:p-3 prose-th:border-b prose-th:border-slate-200 prose-th:text-left
                prose-td:p-3 prose-td:border-b prose-td:border-slate-100 prose-td:text-slate-700
                prose-strong:text-slate-900 prose-strong:font-semibold
                prose-em:text-slate-600 prose-em:italic
                prose-a:font-medium prose-a:no-underline hover:prose-a:underline
                prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-[13px] prose-code:text-slate-800 prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
                prose-blockquote:border-l-4 prose-blockquote:border-slate-300 prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-slate-600
                print:shadow-none print:border-0 print:p-0`}
              dangerouslySetInnerHTML={{ __html: displayedHtml }}
              data-testid="guide-content"
            />
          )}

          {!loading && !error && (
            <footer className="max-w-[760px] mx-auto mt-16 pt-8 border-t border-slate-200 text-sm text-slate-500 print:hidden">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p>© {new Date().getFullYear()} Baobab Logistics</p>
                <p>
                  Une question&nbsp;?{' '}
                  <a href="mailto:support@baobabco.net" className={accent.link}>
                    support@baobabco.net
                  </a>
                </p>
              </div>
            </footer>
          )}
        </main>

        {/* Right "On this page" — visible XL only */}
        <aside className="hidden xl:block w-56 flex-shrink-0 py-12 print:hidden">
          <div className="sticky top-20">
            {!loading && !error && (
              <OnThisPage
                items={fullToc}
                activeSlug={activeSlug}
                onClick={scrollToSection}
                accentColor={accentColor}
              />
            )}
          </div>
        </aside>
      </div>

      {/* Back to top */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`fixed bottom-6 right-6 ${accent.backToTop} text-white p-3 rounded-full shadow-lg transition-all hover:scale-105 z-40 print:hidden`}
          aria-label="Retour en haut"
          data-testid="back-to-top-btn"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default GuideRenderer;
